import { h } from "vue";
import { Modal, message } from "ant-design-vue";

import pb from "@pb/index.json";
import { rechargeTipTime } from "@/config";
import * as Accounts from "@js/account/index";
import ERROR from "@js/error";
import { isSupportPlay } from "@js/utils";
import BusinessManager from "@js/controls";
import { checkCanPlayGame, getGameRiskTips } from "@api/game";
import {
  needChangeGameInQueueOrGame,
  supportHightLevel,
} from "@js/game/game-strategy";
import {
  gameWind,
  checkGameWindOpen,
  handleOpenGameWind,
} from "@js/game/game-window";
import { usePlayGameStore } from "@/store/playGame";
import { useLayoutDataStore } from "@/store/layoutData";
import { useloginStore } from "@/store/login";

const yunpb = pb.nested.yunpb.nested;
const NodeErrorCode = yunpb.NodeErrorCode.values;
const PlayerStatus = yunpb.PlayerStatus.values;

const showToastMessage = (msg: string) => {
  message.destroy();
  message.error(msg, 3);
};

class InterceptChain {
  public next: InterceptChain | null = null;
  public jump: InterceptChain | null = null;

  public intercept(...args: any[]) {
    this.next && this.next.intercept(...args);
  }

  public setJumpIntercept(intercept: InterceptChain) {
    this.jump = intercept;
  }

  public toJumpIntercept(...args: any[]) {
    this.jump && this.jump.intercept(...args);
  }
}

export class PlayGameInterceptChain {
  private firstIntercept: InterceptChain | null = null;
  static nextIntercept: InterceptChain | null = null;

  add(interceptChain: InterceptChain) {
    if (!this.firstIntercept) {
      this.firstIntercept = interceptChain;
      return;
    }

    let node = this.firstIntercept;

    while (node) {
      if (!node.next) {
        node.next = interceptChain;
        break;
      }
      node = node.next;
    }
  }

  start(...args: any[]) {
    this.firstIntercept && this.firstIntercept.intercept(...args);
  }
}

// 检查前置条件
export class CheckCanPlayGame extends InterceptChain {
  intercept(game: pro.yunpb.IGameNode) {
    const layoutDataStore = useLayoutDataStore();
    const loginStore = useloginStore();
    if (!game) {
      showToastMessage("Missing game parameters");
      return;
    }
    // 没网
    if (!navigator.onLine) {
      Modal.confirm({
        title: "Tips",
        content:
          "There is something wrong with the network. Please check it and try again.",
        okText: "OK",
        cancelText: "Have a stroll",
        centered: true,
        onOk: () => {
          console.log("好的");
        },
      });
      return;
    }
    // 验证用户所用浏览器
    if (!isSupportPlay) {
      layoutDataStore.downlaodBrowserModal = true;
      return;
    }
    // 用户是否登录，没有登陆则弹出登录框
    if (!Accounts.isLogin()) {
      loginStore.isShow = true;
      return;
    }
    // 用户是否绑定手机，没有弹窗绑定手机
    // if (userInfo && userInfo.player && !userInfo.player.phone) {
    //   yield put({
    //     type: 'layoutData/updateState',
    //     payload: { bindModal: true },
    //   });
    //   return;
    // }
    // const {
    //   loginState: { userInfo, isBvip, restTime },
    //   store: { addTimeCard },
    // } = store.getState();

    // 验证权限，只有白名单列表内用户可以体验
    const WhiteListType = 4;
    // if (game?.vipLevel === WhiteListType) {
    //   const whiteList: number[] = game.whiteList
    //     ? game.whiteList.split(",").map((id) => Number(id))
    //     : [];
    //   if (!whiteList.includes(userInfo?.player?.id)) {
    //     ElMessage.error("本游戏正在内测中");
    //     return;
    //   }
    // }
    // 用户可用时长不足时，弹窗购买加时卡
    // if (!isBvip && restTime < rechargeTipTime) {
    //   dispatch({
    //     type: "store/updateState",
    //     payload: {
    //       goodsInfoModal: true,
    //       goodsInfo: addTimeCard,
    //       goodsInfoTips: "可用时长不足，是否立即购买加时卡",
    //     },
    //   });
    //   return;
    // }

    Accounts.setSelectGame(game);
    super.intercept(game);
  }
}

// 高配选择
export class SelectGameConfig extends InterceptChain {
  async intercept(game: pro.yunpb.IGameNode = Accounts.getSelectGame()) {
    const playGameStore = usePlayGameStore();
    const playingGame: pro.yunpb.IGameNode = Accounts.getCurrentGame();
    const _supportHightLevel = supportHightLevel(game);
    console.log("supportHightLevel", _supportHightLevel);

    if (Accounts.gameEqual(playingGame, game)) {
      super.intercept();
      return;
    }

    if (_supportHightLevel) {
      playGameStore.gameConfigModal = true;
      PlayGameInterceptChain.nextIntercept = this.next;
      return;
    }

    playGameStore.selectGameIsHighLevel = false;
    super.intercept(false);
  }
}

// 维护提示 风险提示
export class GetGameTips extends InterceptChain {
  async intercept(selectGameIsHighLevel = false) {
    const playGameStore = usePlayGameStore();
    const game: pro.yunpb.IGameNode = Accounts.getSelectGame();
    const playingGame: pro.yunpb.IGameNode = Accounts.getCurrentGame();

    if (Accounts.gameEqual(playingGame, game)) {
      super.intercept();
      return;
    }

    try {
      // 维护提示
      const checkCanPlayGameRes: pro.yunpb.ICheckCanPlayGameRes =
        await checkCanPlayGame({
          gameId: game.gameId,
          isHighLevel: selectGameIsHighLevel,
        });
      console.log("checkCanPlayGame", checkCanPlayGameRes);

      // 风险提示
      const { tips } = await getGameRiskTips({
        gameId: game.gameId,
        isHighLevel: selectGameIsHighLevel,
      });
      console.log("getGameRiskTipsRes", tips);

      if (!tips) {
        super.intercept();
        return;
      }
      Modal.confirm({
        title: "",
        content: h("div", {
          dangerouslySetInnerHTML: { __html: tips },
        }),
        okText: "Game Start",
        cancelText: "Cancle",
        centered: true,
        onOk: () => {
          super.intercept();
        },
      });
    } catch (err: any) {
      console.log("checkCanPlayGame err ", err);
      // 实名认证
      const NODE_ERROR_CODE_REAL_NAME = 1110005;
      if (err?.rpcOut.ret === NODE_ERROR_CODE_REAL_NAME) {
        playGameStore.verificationRealNameModel = true;
      } else if (err?.rpcOut?.ret === NodeErrorCode.ErrNodeGameMaintenance) {
        // Modal.info({
        //   title: "维护提示",
        //   content: React.createElement("div", {
        //     dangerouslySetInnerHTML: {
        //       __html: err?.rpcOut?.desc || "游戏维护中，请稍后再来吧",
        //     },
        //   }),
        //   centered: true,
        //   okText: "我知道了",
        // });
      } else {
        showToastMessage(
          err?.rpcOut?.desc || "该游戏暂时不能游玩，请稍后再来吧"
        );
      }
    }
  }
}

// 是否正在排队or游戏中
export class CheckInQueueOrGame extends InterceptChain {
  async intercept() {
    const playGameStore = usePlayGameStore();
    const game: pro.yunpb.IGameNode = Accounts.getSelectGame();
    const playingGame: pro.yunpb.IGameNode = Accounts.getCurrentGame();

    const isQueueing = playGameStore.isQueueing;

    // 没有正在进行的游戏
    if (!playingGame) {
      // super.toJumpIntercept(game);
      super.intercept(game);
      return;
    }

    // 正在排队，切换游戏
    if (isQueueing) {
      if (Accounts.gameEqual(playingGame, game)) {
        playGameStore.showQueueModal({ playerOper: PlayerStatus.NO_OPER });
      } else {
        playGameStore.showExitModal({ from: playingGame.name, to: game.name });
        PlayGameInterceptChain.nextIntercept = this.next;
      }
      return;
    }

    // 正在玩游戏，切换游戏
    if (!Accounts.gameEqual(playingGame, game)) {
      playGameStore.showExitModal({ from: playingGame.name, to: game.name });
      PlayGameInterceptChain.nextIntercept = this.next;
    } else {
      // 直接打开游戏
      playGameStore.handleOpenGame();
    }
  }
}

// 用户确认切换游戏
export class ChangeGame extends InterceptChain {
  // async intercept() {
  //   const playGameStore = usePlayGameStore();
  //   playGameStore.resetModalState();
  //   const fromGame: pro.yunpb.IGameNode = Accounts.getCurrentGame();
  //   const toGame: pro.yunpb.IGameNode = Accounts.getSelectGame();
  //   const isQueueing = playGameStore.isQueueing;
  //   const currGameIsHighLevel = playGameStore.currGameIsHighLevel;
  //   const selectGameIsHighLevel = playGameStore.selectGameIsHighLevel;
  //   if (!fromGame || !toGame) {
  //     showToastMessage(ERROR.errorMessage(""));
  //     return;
  //   }
  //   // 排队切换游戏
  //   if (isQueueing) {
  //     if (
  //       needChangeGameInQueueOrGame(
  //         fromGame,
  //         toGame,
  //         currGameIsHighLevel,
  //         selectGameIsHighLevel
  //       )
  //     ) {
  //       await playGameStore.handleCancelQueue();
  //     }
  //     super.intercept();
  //     return;
  //   }
  //   // 游戏中切换游戏
  //   if (
  //     needChangeGameInQueueOrGame(
  //       fromGame,
  //       toGame,
  //       currGameIsHighLevel,
  //       selectGameIsHighLevel
  //     )
  //   ) {
  //     await playGameStore.handleGameExit();
  //     super.intercept(toGame);
  //   } else {
  //     const ret: WebSocketRes<pro.yunpb.IChangeGameRes> =
  //       (await BusinessManager.ChangeGame(
  //         toGame.gameId as number,
  //         selectGameIsHighLevel
  //       )) as WebSocketRes<pro.yunpb.IChangeGameRes>;
  //     if (ret && ret.code === 0 && ret.obj && ret.obj.gameNode) {
  //       Accounts.setCurrentGame(ret.obj.gameNode);
  //       playGameStore.showFloatModal({ playerOper: PlayerStatus.NO_OPER });
  //       playGameStore.currGameIsHighLevel = selectGameIsHighLevel;
  //       // 通过游戏通道切换游戏
  //       if (checkGameWindOpen()) {
  //         // 直接切换
  //         gameWind && gameWind.focus();
  //       } else {
  //         // 打开窗口
  //         handleOpenGameWind();
  //       }
  //     } else {
  //       showToastMessage(ERROR.errorMessage(ret.obj));
  //     }
  //   }
  // }
}

// 联机选区
export class SelectServerArea extends InterceptChain {
  async intercept(game: pro.yunpb.IGameNode = Accounts.getSelectGame()) {
    const playGameStore = usePlayGameStore();
    if (!game.isOnlineGame) {
      super.intercept(game);
      return;
    }
    const selectGameIsHighLevel = playGameStore.selectGameIsHighLevel;
    playGameStore.showAreaListModal({
      gameId: game.gameId,
      category: game.category,
      isHighLevel: selectGameIsHighLevel,
    });
    PlayGameInterceptChain.nextIntercept = this.next;
  }
}

// 开始游戏
export class PlayGame extends InterceptChain {
  async intercept(
    game: pro.yunpb.IGetGameRoomInfoRsp = Accounts.getSelectGame()
  ) {
    const playGameStore = usePlayGameStore();
    playGameStore.handleEnterGame(game);
  }
}
